import { __rest } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import styled from '@emotion/styled';
import { getStyle } from './utils.es.js';
import { Alignment } from '@teko-builder/types/shared';
import React, { createContext, useState, useContext } from 'react';
import { H as Heading } from './Heading-e29a867a.js';
import { P as PbIcon } from './PbIcon-311bf624.js';

const AccordionContext = createContext({
    activeKey: null,
    setActiveKey: null,
});
const AccordionProvider = (_a) => {
    var { children } = _a, rest = __rest(_a, ["children"]);
    const [activeKey, setActiveKey] = useState();
    return (jsx(AccordionContext.Provider, Object.assign({ value: Object.assign(Object.assign({}, rest), { activeKey, setActiveKey }) }, { children: children })));
};

const getAccordionItemChildrenCss = ({ headerStyle, contentStyle, contentAlign, }) => {
    const getHorizontalAlign = () => {
        if (!contentAlign)
            return '';
        let alignStyle = 'stretch';
        if (contentAlign === Alignment.center)
            alignStyle = 'center';
        if (contentAlign === Alignment.right)
            alignStyle = 'flex-end';
        return `align-items: ${alignStyle};`;
    };
    return `
  .accordion {
    &__header {
      ${getStyle(headerStyle)}
    }

    &__content {
      ${getStyle(contentStyle)}
      ${getHorizontalAlign()}
    }
  }
`;
};

const Accordion = (_a) => {
    var { children, icon } = _a, rest = __rest(_a, ["children", "icon"]);
    return (jsx(Wrapper$2, Object.assign({}, rest, { children: jsx(AccordionProvider, Object.assign({ icon: icon }, { children: children })) })));
};
Accordion.displayName = 'Accordion';
const Wrapper$2 = styled.div(({ commonStyle, itemStyle, titleStyle, duration }) => {
    const animationDuration = typeof duration === 'number' ? `transition: all ${duration}s;` : '';
    return `
    ${getStyle(commonStyle)}
    display: flex;
    flex-direction: column;

    .accordion {
      &__item {
        ${getStyle(itemStyle)}
      }
      &__title {
        h1, h2, h3, h4, h5, h6 {
          ${getStyle(titleStyle)}
        }
      }
      &__header,
      &__content  {
        ${animationDuration}
      }
    }
  `;
}, getAccordionItemChildrenCss);

const AccordionItem = React.forwardRef((_a, ref) => {
    var { children, customLabel, title, itemKey, icon } = _a, rest = __rest(_a, ["children", "customLabel", "title", "itemKey", "icon"]);
    const { icon: defaultIcon, activeKey, setActiveKey, } = useContext(AccordionContext);
    const isActive = activeKey === itemKey;
    const _b = {
        name: isActive
            ? (icon === null || icon === void 0 ? void 0 : icon.activeName) || (defaultIcon === null || defaultIcon === void 0 ? void 0 : defaultIcon.activeName)
            : (icon === null || icon === void 0 ? void 0 : icon.name) || (defaultIcon === null || defaultIcon === void 0 ? void 0 : defaultIcon.name),
        size: (icon === null || icon === void 0 ? void 0 : icon.size) || (defaultIcon === null || defaultIcon === void 0 ? void 0 : defaultIcon.size),
        color: (icon === null || icon === void 0 ? void 0 : icon.color) || (defaultIcon === null || defaultIcon === void 0 ? void 0 : defaultIcon.color),
        align: (icon === null || icon === void 0 ? void 0 : icon.align) || (defaultIcon === null || defaultIcon === void 0 ? void 0 : defaultIcon.align),
    }, { align = Alignment.right } = _b, restIconProps = __rest(_b, ["align"]);
    return (jsxs(Wrapper$1, Object.assign({ ref: ref }, rest, { className: `accordion__item ${isActive ? 'active' : 'collapsed'}` }, { children: [jsxs("div", Object.assign({ ref: ref, onClick: () => {
                    if (isActive) {
                        setActiveKey(null);
                    }
                    else {
                        setActiveKey(itemKey);
                    }
                }, className: `accordion__header ${align}` }, { children: [customLabel ? (React.cloneElement(customLabel, {
                        className: 'accordion__title',
                    })) : (jsx(Heading, { text: title, className: "accordion__title" })), jsx(PbIcon, Object.assign({}, restIconProps))] })), children] })));
});
AccordionItem.displayName = 'AccordionItem';
const Wrapper$1 = styled.div((_a) => {
    var { commonStyle, iconAlign } = _a, rest = __rest(_a, ["commonStyle", "iconAlign"]);
    return `
    &.accordion__item {
      ${getStyle(commonStyle)}
      ${getAccordionItemChildrenCss(rest)}
    }

    .accordion {
      &__header {
        display: flex;
        align-items: center;
        cursor: pointer;

        &.left {
          flex-direction: row-reverse;
          justify-content: flex-end;
        }
        &.right {
          flex-direction: row;
          justify-content: space-between;
        }
      }

      &__content {
        display: flex;
        flex-direction: column;
      }
    }

    &.collapsed .accordion__content {
      padding-top: 0 !important;
      padding-bottom: 0 !important;
      height: 0;
      opacity: 0;
      pointer-events: none;
    }

    &.active .accordion__content {
      opacity: 1;
    }
  `;
});

const AccordionContent = React.forwardRef((_a, ref) => {
    var rest = __rest(_a, ["contentRef"]);
    return jsx(Wrapper, Object.assign({ ref: ref }, rest, { className: "accordion__content" }));
});
AccordionContent.displayName = 'AccordionContent';
const Wrapper = styled.div(({ commonStyle }) => `
  ${getStyle(commonStyle)}
`);

export { Accordion as A, AccordionItem as a, AccordionContent as b };
